/**
 * SmartGuides.js
 * 
 * Shows dynamic guide lines only when cursor forms 90° or 45° angles
 * with previous or existing points (similar to Aurora Solar behavior).
 */

import * as THREE from "three";
import { LINES } from "../../Global/materials";
import { DrawingGlobals, VERTICES } from "../../Global/variables";

let guidesInitialized = false;

/**
 * Initialize 2 reusable dashed guide lines
 */
export function initSmartGuides(scene) {
    if (guidesInitialized) return;

    const material = new THREE.LineDashedMaterial({
        color: 0xffa500, // orange
        dashSize: 0.3,
        gapSize: 0.2,
        opacity: 0.8,
        transparent: true,
        depthWrite: false,
    });

    const createGuide = () => {
        const len = 500;
        const geo = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-len, 0, 0),
            new THREE.Vector3(len, 0, 0),
        ]);
        const line = new THREE.Line(geo, material.clone());
        line.visible = false;
        line.computeLineDistances();
        scene.add(line);
        return line;
    };

    LINES.smartGuides = {
        primary: createGuide(),
        secondary: createGuide(),
    };

    guidesInitialized = true;
}

/**
 * Update smart guiding lines based on current cursor
 */
export function updateSmartGuides(point) {
    if (!LINES.smartGuides) return;
    const { primary, secondary } = LINES.smartGuides;
    primary.visible = secondary.visible = false; // reset visibility

    const points = DrawingGlobals.intersectionPoints;
    if (points.length < 1) return;

    const base = points.at(-1);
    const dir = new THREE.Vector2(point.x - base.x, point.y - base.y).normalize();
    const tolerance = 0.05; // ≈3°

    // Check 90° and 45° from previous segment
    if (points.length >= 2) {
        const prev = points.at(-2);
        const prevDir = new THREE.Vector2(base.x - prev.x, base.y - prev.y).normalize();
        const angle = Math.acos(prevDir.dot(dir));

        if (isNear(angle, Math.PI / 2, tolerance)) {
            showGuide(primary, base, Math.atan2(prevDir.y, prevDir.x) + Math.PI / 2);
        } else if (isNear(angle, Math.PI / 4, tolerance) || isNear(angle, (3 * Math.PI) / 4, tolerance)) {
            showGuide(primary, base, Math.atan2(prevDir.y, prevDir.x) + Math.PI / 4);
        }
    }

    // Check against all existing vertices (for global alignments)
    for (let group of VERTICES) {
        for (let v of group) {
            const vector = new THREE.Vector2(v.position.x - point.x, v.position.y - point.y);
            const angle = Math.abs(Math.atan2(vector.y, vector.x));
            if (isNear(angle, 0, tolerance) || isNear(angle, Math.PI / 2, tolerance)) {
                showGuide(secondary, v.position, angle);
                return; // only show one global guide
            }
        }
    }
}

/**
 * Helper: Show one guide line from origin with given rotation
 */
function showGuide(line, origin, angle) {
    line.position.copy(origin);
    line.rotation.z = angle;
    line.visible = true;
}

/**
 * Helper: Check if angle within tolerance
 */
function isNear(value, target, tol) {
    return Math.abs(value - target) <= tol;
}

/**
 * Hide all smart guides
 */
export function hideSmartGuides() {
    if (!LINES.smartGuides) return;
    Object.values(LINES.smartGuides).forEach(line => (line.visible = false));
}
